'use client';

import { register } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, boolean as __eliadmin_zod_2 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { Field } from '@eliadmin/core/src/components/form/field';
import { LiaEnvelope } from 'react-icons/lia';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa';
import { BsKey } from 'react-icons/bs';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { useQuery } from '@tanstack/react-query';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { MdOutlineLocalPhone } from 'react-icons/md';

export const registerSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
      name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      password: __eliadmin_zod_1().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      password_confirmation: __eliadmin_zod_1().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      company_buy: __eliadmin_zod_2().optional(),
      gdpr: __eliadmin_zod_2().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      newsletter: __eliadmin_zod_2().optional(),
      ico: __eliadmin_zod_1().optional(),
      dic: __eliadmin_zod_1().optional(),
      ic_dph: __eliadmin_zod_1().optional(),
      company: __eliadmin_zod_1().optional(),
      email_company: __eliadmin_zod_1().optional(),
      address_company: __eliadmin_zod_1().optional(),
      street_number_company: __eliadmin_zod_1().optional(),
      postcode_company: __eliadmin_zod_1().optional(),
      city_company: __eliadmin_zod_1().optional(),
      country_company: __eliadmin_zod_0({ id: __eliadmin_zod_1() }).optional(),
      newsletter_return_url: __eliadmin_zod_1(),
      tos: __eliadmin_zod_2().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      'g-recaptcha-response': __eliadmin_zod_1().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

type RegisterFormProps = {
  initialSocialAuthProviders?: SocialAuthProviderItem[];
};

export function RegisterForm({ initialSocialAuthProviders }: RegisterFormProps) {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof registerSchema>>>({
    resolver: zodResolver(registerSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      ico: '',
      dic: '',
      ic_dph: '',
      company: '',
      address_company: '',
      street_number_company: '',
      postcode_company: '',
      city_company: '',
      country_company: { id: '' },
      email_company: '',
      company_buy: false,
      gdpr: false,
      newsletter: false,
      tos: false,
      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
    },
  });

  const { toast } = useToast();
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    /** Check password validity */
    if (data.password !== data.password_confirmation) {
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    try {

      const resp = await register({ ...data,  locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text654'),
        variant: 'success',
      });
      router.replace(ROUTES.REGISTER_CONFIRM());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text653'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const handlePasswordConfirmationMouseDown = () => {
    setShowPasswordConfirmation(true);
  };

  const handlePasswordConfirmationMouseUp = () => {
    setShowPasswordConfirmation(false);
  };

  const isCompany = form.watch('company_buy');

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  return (
    <Form {...form}>
      <form
        className={cn(
          'flex w-full flex-col items-center justify-center gap-4 sm:gap-6 lg:gap-10 xl:gap-12',
          'ea-register-form',
        )}
        onSubmit={handleSubmit}
      >
        <FormSection title={t('text385')}>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem
                required
                className={cn('col-span-12 w-full', 'ea-register-form__name-field')}
              >
                <FormControl>
                  <Input
                    placeholder={`${t('text35')} *`}
                    type="email"
                    {...field}
                    before={<LiaEnvelope size={24} className="text-[#636363]" />}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="col-span-12 flex w-full flex-col items-start justify-start gap-2">
            <div className="grid w-full grid-cols-12 gap-4 sm:gap-5 lg:gap-6">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem
                    required
                    className={cn(
                      'col-span-12 w-full sm:col-span-6',
                      'ea-sign-in-form__password-field',
                    )}
                  >
                    <FormControl className={cn('w-full', 'ea-sign-in-form__password-input')}>
                      <div className="relative">
                        <Input
                          placeholder={`${t('text191')} *`}
                          type={showPassword ? 'text' : 'password'}
                          before={<BsKey size={24} className="text-[#636363]" />}
                          after={
                            <button
                              type="button"
                              onMouseDown={handlePasswordMouseDown}
                              onMouseUp={handlePasswordMouseUp}
                              onMouseLeave={handlePasswordMouseUp}
                              onTouchStart={handlePasswordMouseDown}
                              onTouchEnd={handlePasswordMouseUp}
                            >
                              {showPassword ? (
                                <FaRegEyeSlash size={20} className="text-primary" />
                              ) : (
                                <FaRegEye size={20} className="text-primary" />
                              )}
                            </button>
                          }
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage className={cn('', 'ea-sign-in-form__password-message')} />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password_confirmation"
                render={({ field }) => (
                  <FormItem
                    required
                    className={cn(
                      'col-span-12 w-full sm:col-span-6',
                      'ea-register-form__password-confirmation-field',
                    )}
                  >
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder={`${t('text263')} *`}
                          type={showPasswordConfirmation ? 'text' : 'password'}
                          {...field}
                          before={<BsKey size={24} className="text-[#636363]" />}
                          after={
                            <button
                              type="button"
                              onMouseDown={handlePasswordConfirmationMouseDown}
                              onMouseUp={handlePasswordConfirmationMouseUp}
                              onMouseLeave={handlePasswordConfirmationMouseUp}
                              onTouchStart={handlePasswordConfirmationMouseDown}
                              onTouchEnd={handlePasswordConfirmationMouseUp}
                            >
                              {showPasswordConfirmation ? (
                                <FaRegEyeSlash size={20} className="text-primary" />
                              ) : (
                                <FaRegEye size={20} className="text-primary" />
                              )}
                            </button>
                          }
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <p className="w-full text-xs text-[#636363]">{t('text2349')}</p>
          </div>
        </FormSection>

        <Separator />

        <FormSection title={t('text2350')}>
          <FormField
            control={form.control}
            name="company_buy"
            render={({ field }) => (
              <FormItem className="col-span-12 w-full">
                <FormControl>
                  <RadioGroup
                    onValueChange={value => field.onChange(value === 'true')}
                    defaultValue={field.value ? 'true' : 'false'}
                    className="flex w-full items-center justify-start gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="notCompany" className="border-border" />
                      <Label
                        htmlFor="notCompany"
                        className="cursor-pointer whitespace-nowrap text-sm font-normal text-black sm:text-base"
                      >
                        {t('text2234')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="isCompany" className="border-border" />
                      <Label
                        htmlFor="isCompany"
                        className="sm:text-bas whitespace-nowrape cursor-pointer text-sm font-normal text-black"
                      >
                        {t('text1432')}
                      </Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {isCompany ? (
            <>
              <Field
                control={form.control}
                name="ico"
                placeholder={`${t('text42')} *`}
                required
                className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
              />
              <Field
                control={form.control}
                name="dic"
                placeholder={`${t('text43')} *`}
                required
                className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
              />
              <Field
                control={form.control}
                name="ic_dph"
                placeholder={t('text44')}
                className={{ wrapper: 'col-span-12 w-full' }}
              />
              <Field
                control={form.control}
                name="name"
                placeholder={`${t('text33')} *`}
                required
                className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
              />
              <Field
                control={form.control}
                name="surname"
                placeholder={`${t('text34')} *`}
                required
                className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
              />
              <Field
                control={form.control}
                name="company"
                placeholder={`${t('text1188')} *`}
                required
                className={{ wrapper: 'col-span-12 w-full' }}
              />
              <Field
                control={form.control}
                name="address_company"
                placeholder={`${t('text38')} *`}
                required
                className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
              />
              <Field
                control={form.control}
                name="street_number_company"
                placeholder={`${t('text2351')} *`}
                required
                className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
              />
              <Field
                control={form.control}
                name="postcode_company"
                placeholder={`${t('text40')} *`}
                required
                className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
              />
              <Field
                control={form.control}
                name="city_company"
                placeholder={`${t('text39')} *`}
                required
                className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
              />
              <Field
                control={form.control}
                type="select"
                name="country_company.id"
                placeholder={t('text519')}
                className={{ wrapper: 'col-span-12 w-full' }}
                isDisabled={isCountriesLoading}
                options={countries.data.map(c => ({
                  value: c.id,
                  label: c.name,
                }))}
              />
            </>
          ) : (
            <>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem
                    required
                    className={cn(
                      'col-span-12 w-full sm:col-span-6',
                      'ea-register-form__name-field',
                    )}
                  >
                    <FormControl>
                      <Input placeholder={`${t('text33')} *`} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="surname"
                render={({ field }) => (
                  <FormItem
                    required
                    className={cn(
                      'col-span-12 w-full sm:col-span-6',
                      'ea-register-form__surname-field',
                    )}
                  >
                    <FormControl>
                      <Input placeholder={`${t('text34')} *`} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
        </FormSection>

        <Separator />

        <FormSection title={t('text360')}>
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem
                required
                className={cn(
                  'w-full',
                  isCompany ? 'col-span-12 sm:col-span-6' : 'col-span-12 sm:col-span-12',
                  'ea-register-form__name-field',
                )}
              >
                <FormControl>
                  <Input
                    placeholder={`${t('text36')} *`}
                    type="text"
                    {...field}
                    before={<MdOutlineLocalPhone size={24} className="text-[#636363]" />}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {isCompany && (
            <FormField
              control={form.control}
              name="email_company"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'w-full',
                    isCompany ? 'col-span-12 sm:col-span-6' : 'col-span-12 sm:col-span-12',
                    'ea-register-form__name-field',
                  )}
                >
                  <FormControl>
                    <Input
                      placeholder={`${t('text35')} *`}
                      type="email"
                      {...field}
                      before={<LiaEnvelope size={24} className="text-[#636363]" />}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </FormSection>

        <div
          className={cn('flex flex-col items-start space-y-4 pb-12', 'ea-register-form__footer')}
        >
          <FormField
            control={form.control}
            name="tos"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__tos-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  {t('text264')}{' '}
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.TOS?.(), locale, 'tos')}
                    className="underline"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text265')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__gdpr-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(
                      ROUTES?.GDPR?.() ?? '/',
                      locale,
                      'gdpr',
                    )}
                    className="underline"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text364')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className={cn('mx-auto w-full sm:w-fit', 'ea-register-form__submit-button')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text192')}
          </Button>
          <div className="text-xs sm:text-sm">
            {/* TODO LINKS */}
            {t('text625')}{' '}
            <Link
              href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.TOS?.() ?? '/', locale, 'tos')}
              className="text-primary"
              prefetch={false}
            >
              {t('text626')}
            </Link>{' '}
            {t('text627')}{' '}
            <Link
              href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr')}
              className="text-primary"
              prefetch={false}
            >
              {t('text628')}
            </Link>
          </div>
        </div>
      </form>
    </Form>
  );
}

function FormSection({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="grid w-full grid-cols-12 gap-4 sm:gap-5 lg:gap-6">
      {title && (
        <h6 className="col-span-12 text-xl font-medium tracking-tight sm:text-2xl">{title}</h6>
      )}
      {children}
    </div>
  );
}

function Separator({ className }: { className?: string }) {
  return <div className={cn('col-span-12 h-[1px] w-full flex-grow bg-black/10', className)} />;
}
